import {
  ChevronDownIcon,
  HomeLineIcon,
  SearchLgIcon,
  User02Icon,
} from '@langchain/untitled-ui-icons';
import { Link, Typography } from '@mui/joy';

import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { BreadcrumbProps } from '@/components/Breadcrumbs.types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { ProfileCircle } from '@/components/ProfileCircle';
import {
  useCurrentOrganization,
  useSelectedWorkspace,
  useWorkspaceList,
} from '@/hooks/useSwr';
import { appHubIndexPath, appOrganizationPath } from '@/utils/constants';
import { lowerCaseIncludes } from '@/utils/stringUtils';
import { cn } from '@/utils/tailwind';
import { getTenantDisplayName } from '@/utils/tenants-format';

export function OrganizationCrumb(props: Partial<BreadcrumbProps>) {
  const workspaceList = useWorkspaceList();

  const [search, setSearch] = useState('');

  const selectedWorkspace = useSelectedWorkspace();
  const selected = selectedWorkspace;
  const { data: organization } = useCurrentOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const isHub = location.pathname.startsWith(`/${appHubIndexPath}`);
  const showTenantHandleInCrumb = isHub;
  const [searchParams, setSearchParams] = useSearchParams();

  const isPersonal = selected.data?.is_personal;
  const workspaces = workspaceList.data;

  const hasMoreThanOneWorkspace = (workspaces?.length || 0) > 1;
  if (!workspaces?.length) {
    return props.condensed ? (
      <div className="flex items-center gap-1 text-xs">
        <HomeLineIcon className="size-4" />
      </div>
    ) : (
      <div className="flex items-center gap-1 text-xs">
        <HomeLineIcon className="size-4" />
        <Typography
          whiteSpace="pre"
          textOverflow="ellipsis"
          fontSize="small"
          sx={{
            overflowX: 'clip',
            maxWidth: 200,
            marginLeft: 0.75,
          }}
        >
          Home
        </Typography>
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger
        className={cn(
          'group rounded-lg',
          hasMoreThanOneWorkspace && 'cursor-pointer'
        )}
      >
        <Link
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            flexShrink: 1,
            gap: 0.75,
            textDecoration: 'none',
            color: props.isActive
              ? theme.palette.text.primary
              : theme.palette.text.tertiary,
            ':hover': {
              textDecorationLine: 'none',
            },
            cursor: 'pointer',
          })}
          fontSize="small"
          onClick={!hasMoreThanOneWorkspace ? () => navigate('/') : undefined}
        >
          <ProfileCircle
            name={
              isPersonal ? '' : organization?.display_name ?? 'organization'
            }
            backupIcon={<User02Icon className="size-3" />}
            className="h-5 w-5 text-xxs"
          />
          {!props.condensed && (
            <>
              <Typography
                whiteSpace="pre"
                textOverflow="ellipsis"
                sx={{
                  overflowX: 'clip',
                  maxWidth: 200,

                  cursor: 'pointer',
                }}
                className="group-hover:underline"
              >
                {showTenantHandleInCrumb
                  ? selected.data?.tenant_handle ??
                    getTenantDisplayName(
                      selected.data,
                      selected.data?.is_personal
                    )
                  : getTenantDisplayName(
                      selected.data,
                      selected.data?.is_personal
                    )}
              </Typography>
              {hasMoreThanOneWorkspace && (
                <ChevronDownIcon className="size-4" />
              )}
            </>
          )}
        </Link>
      </PopoverTrigger>
      {hasMoreThanOneWorkspace && (
        <PopoverContent
          className="max-h-[500px] overflow-y-auto p-2"
          align="start"
        >
          <div className="px-0 pb-2">
            <div className="flex items-center gap-2 rounded-md bg-secondary p-2">
              <SearchLgIcon className="h-4 w-4"></SearchLgIcon>
              <input
                className="w-full border-none bg-transparent p-0 text-sm outline-none"
                type="text"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          {workspaces
            .filter((i) => !showTenantHandleInCrumb || i.tenant_handle)
            .filter(
              (i) =>
                lowerCaseIncludes(i.display_name, search) ||
                lowerCaseIncludes(i.tenant_handle ?? '', search)
            )
            .map((tenant) => {
              return (
                <div
                  key={tenant.id}
                  className={cn(
                    'text-md cursor-pointer select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-secondary',
                    'flex items-center gap-2 rounded-md text-sm',
                    showTenantHandleInCrumb && 'items-start'
                  )}
                  onClick={() => {
                    if (showTenantHandleInCrumb) {
                      setSearchParams({
                        ...searchParams,
                        organizationId: tenant.id,
                      });
                    } else {
                      navigate(`/${appOrganizationPath}/${tenant.id}`);
                    }
                  }}
                >
                  {showTenantHandleInCrumb ? (
                    <span className="flex flex-col">
                      <span className="text-sm">{tenant?.tenant_handle}</span>
                      <span className="text-sm text-tertiary">
                        {getTenantDisplayName(tenant, tenant?.is_personal)}
                      </span>
                    </span>
                  ) : (
                    <span>
                      {getTenantDisplayName(tenant, tenant?.is_personal)}
                    </span>
                  )}
                </div>
              );
            })}
        </PopoverContent>
      )}
    </Popover>
  );
}
